import { useState, useRef, useCallback, useEffect } from 'react';

/**
 * useClipDownload — manage clip downloads with a concurrent cap, as a reusable hook for
 * MCPlayback's function components. Mirrors the legacy PlaybackView.transportDownload behavior
 * (fetch -> blob -> object-URL -> <a> click, with a 5-download cap and transient state entries)
 * but in hook form. Legacy playback keeps its own class method, so this adds no legacy risk.
 *
 * The cap/filename/button-state logic is exported as pure helpers so it's unit-testable without
 * a hook renderer.
 */

export const MAX_CONCURRENT_DOWNLOADS = 5;

/** Download filename (legacy shape): cam non-alnum -> _ collapsed; time slashes/colons stripped, spaces -> _. */
export const buildDownloadFilename = (camName = 'cam', timestamp = '') => {
  const safeCam = camName.replace(/[^a-zA-Z0-9]/g, '_').replace(/_+/g, '_');
  const safeTime = timestamp.replace(/[/:]/g, '').replace(/\s+/g, '_');
  return `${safeCam}_${safeTime || Date.now()}.mp4`;
};

/**
 * A fetched clip response is usable only if it's real video, not an error surface. A 200 can still be
 * an HTML page (expired session / proxy error) or an empty body — saving those produces a "downloaded"
 * file that won't play, so we treat them as failures instead of reporting success.
 */
export const isPlayableVideoResponse = (contentType = '', blobSize = 0) => {
  if (/text\/html/i.test(contentType || '')) return false;
  return (blobSize || 0) > 0;
};

/**
 * Download progress percent (0–100), or null when the total is unknown — Content-Length may be absent
 * (chunked transfer, or a dev proxy that drops it), in which case the UI shows an indeterminate bar.
 * pbserver exposes Content-Length for CORS (handler.c Access-Control-Expose-Headers), so direct requests
 * report a real %; the one-time postoverlay render happens BEFORE the body streams, so it reads as
 * indeterminate first (no bytes yet), then the bar fills during transfer.
 */
export const downloadPercent = (received = 0, total = 0) => {
  if (!total || total <= 0) return null;
  return Math.min(100, Math.max(0, Math.round((received / total) * 100)));
};

/** True when the number of in-flight downloads has reached the cap. */
export const atDownloadCapacity = (downloads = [], max = MAX_CONCURRENT_DOWNLOADS) =>
  downloads.filter(d => d.state === 'downloading').length >= max;

/**
 * Is this response "the OSD burn is still being produced" rather than a real failure?
 *
 * pbserver renders the burn SYNCHRONOUSLY inside the request (handler.c check_overlay forks mpengine
 * `postoverlay` and waitpid()s it), so one `&overlay=1` fetch can occupy the connection for minutes on
 * a large clip, and three different statuses can come back meaning "come back later".
 *
 * STATUS ALONE IS NOT ENOUGH. Both 503s and one 404 are emitted BEFORE the overlay block ever runs:
 *   handler.c:333 404 "File Not Found"            — the clip genuinely is not there
 *   handler.c:539 503 "Auth Unavailable"          — rda-backend is down
 *   handler.c:566 503 "VideoStore unavailable"    — the store is drained
 *   handler.c:606 404 "Overlay in progress"       — RETRY: another request holds the .overlay-tmp lock
 *   handler.c:610 503 "Overlay Service Unavailable" — RETRY: all 4 shared semaphore slots busy
 * Retrying the first three for five minutes would sit on a permanent failure, so match the pbserver
 * message, which is short and overlay-specific.
 *
 * 502 is the exception and needs no body: it comes from APACHE, not pbserver, so there is no message to
 * read. On an overlay URL the only thing that outlasts the proxy timeout is the render, which continues
 * server-side and still caches. (With the /camstream/ timeout=1800 fix this should no longer occur, but
 * an older NVR still has the short default.)
 *
 * MATCH THE STATUS LINE AS WELL AS THE BODY. The two pbserver generations put the message in different
 * places: the C server sends both (`send_error(sock, 404, "Overlaying", "Overlay in progress")` — status
 * text AND body), while the LEGACY Python server on RPM NVRs writes only a bare status line with an EMPTY
 * body (`fetch_thread.py:236` `HTTP/1.1 404 Overlaying\r\n\r\n`). A body-only matcher therefore never
 * retried on an RPM box, which CLAUDE.md still lists as supported.
 *
 * Verified on cs2427 2026-08-17: a 4-camera "Download all" logged 502,502,502,503 and pbserver logged
 * the same four clips 200 four minutes later.
 *
 * @param {number} status  HTTP status
 * @param {string} url     the request URL (must carry overlay=1 for any of this to apply)
 * @param {string} [body]  the response body, when it could be read
 */
const OVERLAY_PENDING_BODY = /overlay(ing| in progress| service unavailable)/i;
export const isOverlayRenderPending = (status, url = '', message = '') => {
  if (!/[?&]overlay=1(&|$)/.test(String(url || ''))) return false;
  if (status === 502) return true;                       // apache proxy timeout — no pbserver message exists
  if (status !== 404 && status !== 503) return false;
  return OVERLAY_PENDING_BODY.test(String(message || '')); // ambiguous status — the message decides
};

/**
 * Backoff between overlay-render retries (ms). Spans ~5 minutes because that is the real order of
 * magnitude of a full-res HEVC postoverlay pass, and the first request can never win when the render
 * outlasts Apache's proxy timeout.
 */
export const OVERLAY_RETRY_DELAYS = [2000, 5000, 10000, 20000, 30000, 45000, 60000, 60000, 60000];

/**
 * Backoff for the AMBIGUOUS statuses — a much shorter budget than a confirmed render, because these can
 * equally mean "permanently broken", and each retry re-forks a full-res transcode:
 *
 *   503 — handler.c maps check_overlay's -2 to one status, and -2 covers FIVE conditions: the overlay
 *         semaphore being full (transient) but also a failed lock create, a failed fork, a NON-ZERO
 *         postoverlay exit, and a failed rename. A corrupt clip fails postoverlay every time, so the
 *         long schedule would re-run an expensive transcode for five minutes and pin a queue slot.
 *   502 — apache's, not pbserver's: a render outrunning the proxy timeout, or a dead/garbage upstream,
 *         with no message to tell them apart. The `/camstream/ timeout=1800` fix removes the first case
 *         on an updated NVR, so this is mostly a bridge for boxes that have not taken that config.
 *
 * Only a 404 whose message names the overlay is unambiguous — check_overlay's -1 means another request
 * is rendering this exact clip RIGHT NOW — and that one keeps the long schedule. #86bb7phhh
 */
export const AMBIGUOUS_RETRY_DELAYS = [1000, 3000, 5000];

/** Aggregate a single button icon state from the downloads list (downloading > error > done > null). */
export const deriveDownloadButtonState = (downloads = []) => {
  if (downloads.some(d => d.state === 'downloading')) return 'downloading';
  if (downloads.some(d => d.state === 'error')) return 'error';
  if (downloads.some(d => d.state === 'done')) return 'done';
  return null;
};

export const useClipDownload = ({
  maxConcurrent = MAX_CONCURRENT_DOWNLOADS,
  fetchImpl,
  overlayRetryDelays = OVERLAY_RETRY_DELAYS,
  ambiguousRetryDelays = AMBIGUOUS_RETRY_DELAYS,
} = {}) => {
  const [downloads, setDownloads] = useState([]);
  const nextId = useRef(0);
  // AbortController per in-flight download id, so the toast ✕ can cancel it mid-flight (David 2026-08-06).
  const controllers = useRef({});
  // Pending overlay-backoff timer per download id, so a cancel (or unmount) can cut a retry sleep short
  // instead of holding its queue slot for the rest of the delay. #86bb7phhh
  const retryTimers = useRef({});
  // Mirror the latest committed downloads so startDownload reads a current count for the cap
  // (matches legacy reading this.state.downloads).
  const downloadsRef = useRef(downloads);
  downloadsRef.current = downloads;
  // SYNCHRONOUS in-flight set. downloadsRef only refreshes on RENDER, so a synchronous burst
  // ("Download all clips" loops over every visible cam in one handler) saw the same stale list on
  // every call and blew straight through the cap — 16 cams meant 16 concurrent pbserver fetches.
  // A Set keyed by download id moves on the call itself, so the cap holds inside the loop, and
  // releasing is idempotent (delete of an absent id is a no-op). #86bb7phhh
  const inFlightIds = useRef(new Set());
  // FIFO of clips waiting for a slot. See the pump below for why bulk downloads queue.
  const queueRef = useRef([]);
  const pumpRef = useRef(() => {});
  const releaseSlot = useCallback((id) => {
    if (retryTimers.current[id]) { clearTimeout(retryTimers.current[id]); delete retryTimers.current[id]; }
    inFlightIds.current.delete(id);
    pumpRef.current(); // a freed slot pulls the next queued clip
  }, []);

  // Unmount guard (#86baz05kd): on unmount, mark unmounted + ABORT every in-flight download so its
  // fetch/body stream stops instead of running to completion, and so the deferred setState wrappers
  // below no-op (no "setState on unmounted component" warning after navigating away from /mcplayback).
  const mountedRef = useRef(true);
  useEffect(() => () => {
    mountedRef.current = false;
    Object.values(controllers.current).forEach((c) => { if (c) { try { c.abort(); } catch (e) { /* ignore */ } } });
    controllers.current = {};
    Object.values(retryTimers.current).forEach((t) => clearTimeout(t));
    retryTimers.current = {};
    queueRef.current = []; // nothing queued should start after we navigate away
  }, []);

  const removeDownload = useCallback((id) => {
    if (!mountedRef.current) return;
    setDownloads(prev => prev.filter(d => d.id !== id));
  }, []);

  const updateDownload = useCallback((id, state, error) => {
    if (!mountedRef.current) return;
    setDownloads(prev => prev.map(d => (d.id === id ? { ...d, state, error } : d)));
  }, []);

  const updateProgress = useCallback((id, progress) => {
    if (!mountedRef.current) return;
    setDownloads(prev => prev.map(d => (d.id === id ? { ...d, progress } : d)));
  }, []);

  const addDownload = useCallback((filename, state, error) => {
    const id = ++nextId.current;
    const entry = { id, filename, state, progress: null };
    if (error) entry.error = error;
    setDownloads(prev => [...prev, entry]);
    if (state === 'done') setTimeout(() => removeDownload(id), 3000);
    if (state === 'error') setTimeout(() => removeDownload(id), 5000);
    return id;
  }, [removeDownload]);

  // Runs ONE download. Admission is the pump's job — by the time this is called a slot is already held.
  const runDownload = useCallback((src, { camName = 'cam', timestamp = '' } = {}) => {
    const filename = buildDownloadFilename(camName, timestamp);
    const id = addDownload(filename, 'downloading');
    inFlightIds.current.add(id);
    const doFetch = fetchImpl || (typeof fetch !== 'undefined' ? fetch : null);
    if (!doFetch) { releaseSlot(id); updateDownload(id, 'error', 'fetch unavailable'); return; }
    // Abortable so the toast ✕ can cancel mid-flight (aborts the fetch + its body stream).
    const controller = (typeof AbortController !== 'undefined') ? new AbortController() : null;
    controllers.current[id] = controller;

    // Abort-aware backoff. A plain setTimeout kept the slot in inFlightIds for up to a full delay after
    // the user hit ✕, blocking the queue behind a cancelled download. #86bb7phhh
    const wait = (ms) => new Promise((resolve, reject) => {
      const t = setTimeout(resolve, ms);
      retryTimers.current[id] = t;
      if (controller && controller.signal && controller.signal.addEventListener) {
        controller.signal.addEventListener('abort', () => {
          clearTimeout(t);
          const e = new Error('aborted'); e.name = 'AbortError'; reject(e);
        }, { once: true });
      }
    });

    // The pbserver message, wherever that generation puts it: the C server sends a body, the legacy
    // Python one sends only a status line. Join both so either matches. 502 is apache's, so skip it.
    const readMessage = (res) => {
      const statusText = (res && res.statusText) || '';
      if (res.status === 502 || typeof res.text !== 'function') return Promise.resolve(statusText);
      return res.text().then((b) => `${statusText} ${b || ''}`).catch(() => statusText);
    };

    // Drain a successful response to { blob, contentType }, reporting % while it streams.
    const consume = (res) => {
      const contentType = (res.headers && res.headers.get) ? res.headers.get('content-type') : '';
      const total = parseInt((res.headers && res.headers.get) ? res.headers.get('content-length') : '', 10) || 0;
      // Stream the body so we can report progress (% from Content-Length). Fall back to blob() when the
      // body isn't a readable stream (jsdom / older engines) — then progress stays indeterminate.
      if (res.body && typeof res.body.getReader === 'function') {
        const reader = res.body.getReader();
        const chunks = [];
        let received = 0;
        let lastPct = -1;
        const readChunk = () => reader.read().then(({ done, value }) => {
          if (done) return { blob: new Blob(chunks, contentType ? { type: contentType } : {}), contentType };
          chunks.push(value);
          received += (value && value.length) || 0;
          const pct = downloadPercent(received, total);
          if (pct !== null && pct !== lastPct) { lastPct = pct; updateProgress(id, pct); } // one setState per % step
          return readChunk();
        });
        return readChunk();
      }
      return res.blob().then((blob) => ({ blob, contentType }));
    };

    // ONE chain that always resolves to { blob, contentType }. The retry recursion must stay INSIDE this
    // handler: chaining consume() as a separate .then would re-apply it to the recursive call's already
    // consumed result ("res.blob is not a function").
    // Two budgets: a confirmed in-progress render (404) gets the long schedule, the ambiguous statuses
    // (502/503, either of which can mean permanently broken) get a short one.
    const tries = { overlay: 0, ambiguous: 0 };

    const handle = (res) => {
      const maybePending = res.status === 404 || res.status === 502 || res.status === 503;
      return (maybePending ? readMessage(res) : Promise.resolve('')).then((message) => {
        if (isOverlayRenderPending(res.status, src, message)) {
          // Only a 404 "Overlaying" is a CONFIRMED in-progress render (check_overlay -1). 502 and 503
          // are ambiguous and get the short budget — see AMBIGUOUS_RETRY_DELAYS.
          const confirmed = res.status === 404;
          const delays = confirmed ? overlayRetryDelays : ambiguousRetryDelays;
          const n = confirmed ? tries.overlay++ : tries.ambiguous++;
          if (n >= delays.length) {
            throw new Error(confirmed
              ? 'Server is still rendering the overlay for this clip — try again in a moment'
              : `Server returned ${res.status} — the playback server could not produce this clip`);
          }
          return wait(delays[n]).then(() => attempt());
        }
        if (!res.ok) throw new Error(`Server returned ${res.status}`);
        return consume(res);
      });
    };

    function attempt() {
      // cache:'no-store' — pbserver sets no Cache-Control (handler.c send_error), so a browser or
      // intermediary is free to heuristically cache the retryable 404 "Overlay in progress". Every retry
      // would then re-read that cached body and never see the finished file. #86bb7phhh
      const init = { cache: 'no-store' };
      if (controller) init.signal = controller.signal;
      return doFetch(src, init).then((res) => handle(res));
    }

    attempt()
      .then(({ blob, contentType }) => {
        // Don't report success for an error page / empty body dressed as a 200 (would "save" an unplayable file).
        if (!isPlayableVideoResponse(contentType, blob && blob.size)) {
          throw new Error('No video returned for this clip');
        }
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.click();
        setTimeout(() => URL.revokeObjectURL(url), 5000);
        delete controllers.current[id];
        releaseSlot(id);
        updateDownload(id, 'done');
        setTimeout(() => removeDownload(id), 3000);
      })
      .catch(err => {
        delete controllers.current[id];
        releaseSlot(id);
        if (err && err.name === 'AbortError') return; // cancelled via cancelDownload — the entry is already cleared
        updateDownload(id, 'error', err.message || 'Download failed');
        setTimeout(() => removeDownload(id), 5000);
      });
  }, [fetchImpl, addDownload, updateDownload, updateProgress, removeDownload, releaseSlot, overlayRetryDelays, ambiguousRetryDelays]);

  // Admission: drain the queue up to the cap. Queueing (rather than the old "Download limit reached"
  // error) is what makes "Download all clips" work at all — it enqueues every visible cam in one
  // synchronous loop. Draining a few at a time also matters server-side: each overlay download holds one
  // of pbserver's FOUR shared overlay/transcode semaphore slots (main.c sem_init 4) for the entire
  // postoverlay render, so an unthrottled bulk download starves live playback's profile-2 transcodes on
  // the same box. #86bb7phhh
  const pump = useCallback(() => {
    while (inFlightIds.current.size < maxConcurrent && queueRef.current.length) {
      const next = queueRef.current.shift();
      runDownload(next.src, next.opts);
    }
  }, [maxConcurrent, runDownload]);
  pumpRef.current = pump;

  const startDownload = useCallback((src, opts = {}) => {
    if (!src) return;
    queueRef.current.push({ src, opts });
    pumpRef.current();
  }, []);

  // Cancel an in-flight download: abort its fetch (the reader then rejects with AbortError, which the
  // .catch swallows) and drop the toast. No-op once it has finished (the controller is already cleared).
  const cancelDownload = useCallback((id) => {
    const c = controllers.current[id];
    if (c) { try { c.abort(); } catch (e) { /* ignore */ } }
    delete controllers.current[id];
    // Also cut any overlay-backoff sleep directly: without an AbortController (older engines) the abort
    // above cannot reject it, and the slot would stay held for the rest of the delay.
    releaseSlot(id);
    removeDownload(id);
  }, [removeDownload, releaseSlot]);

  return {
    downloads,
    startDownload,
    cancelDownload,
    buttonState: deriveDownloadButtonState(downloads),
  };
};
